// src/components/layout/Sidebar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  styled,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  IconButton,
  Drawer,
  Button,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Login as LoginIcon,
  Menu as MenuIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Chat as ChatIcon,
  Folder as FolderIcon,
  DeleteOutline as DeleteIcon,
  Star as StarIcon,
  GitHub as GitHubIcon,
  Info as InfoIcon,
  ShowChart as ShowChartIcon,
  Settings as SettingsIcon,
  FormatListBulleted as WatchlistIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import AuthDialog from './AuthDialog';
import AccountDialog from './AccountDialog';

// Styled components
const SidebarContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRight: '1px solid rgba(148, 163, 184, 0.1)',
  overflow: 'hidden',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  padding: '20px 24px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  background: 'linear-gradient(135deg, #00D2FF 0%, #0094CC 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
}));

const NewChatButton = styled(Button)(({ theme }) => ({
  margin: '16px 16px 8px',
  padding: '12px 16px',
  justifyContent: 'flex-start',
  background: 'linear-gradient(135deg, #00D2FF 0%, #0094CC 100%)',
  color: '#000000',
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 2px 8px rgba(0, 210, 255, 0.2)',
  '&:hover': {
    background: 'linear-gradient(135deg, #33DBFF 0%, #00A8E6 100%)',
    boxShadow: '0 4px 12px rgba(0, 210, 255, 0.3)',
  }
}));

const SessionList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  padding: theme.spacing(1, 0),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(148, 163, 184, 0.3)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  }
}));

const SessionItem = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0, 1),
  borderRadius: 12,
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(0.5),
  '.MuiListItemText-root': {
    margin: 0,
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(0, 210, 255, 0.1)',
    border: '1px solid rgba(0, 210, 255, 0.2)',
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(148, 163, 184, 0.05)',
  }
}));

const NavItem = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: 12,
  padding: theme.spacing(1.5, 2),
  '.MuiListItemText-root': {
    margin: 0,
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(0, 210, 255, 0.1)',
    border: '1px solid rgba(0, 210, 255, 0.2)',
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    '& .MuiListItemIcon-root': {
      color: theme.palette.primary.main,
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(148, 163, 184, 0.05)',
  }
}));

const Footer = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(148, 163, 184, 0.1)',
}));

export default function Sidebar({ toggleMode }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser } = useAuth();
  const { 
    sessions, 
    activeId, 
    switchSession, 
    newSession,
    deleteSession
  } = useChat();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showAcct, setShowAcct] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, sessionId: null });
  
  const isDark = theme.palette.mode === 'dark';

  const handleDrawerToggle = () => setMobileOpen(o => !o);
  
  const handleNewChat = () => {
    newSession();
    if (isMobile) {
      setMobileOpen(false);
    }
    navigate('/');
  };
  
  const handleSessionClick = (sessionId) => {
    switchSession(sessionId);
    if (isMobile) {
      setMobileOpen(false);
    }
    navigate('/');
  };
  
  const handleDeleteSession = (e, sessionId) => {
    e.stopPropagation();
    setConfirmDelete({ open: true, sessionId });
  };
  
  const confirmDeleteSession = async () => {
    if (confirmDelete.sessionId) {
      await deleteSession(confirmDelete.sessionId);
    }
    setConfirmDelete({ open: false, sessionId: null });
  };
  
  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Truncate session title for display
  const truncateTitle = (title) => {
    return title.length > 25 ? title.substring(0, 22) + '...' : title;
  };

  const sidebarContent = (
    <SidebarContainer>
      <LogoContainer>
        <Logo onClick={() => handleNavigation('/')}>Cricket</Logo>
        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton onClick={toggleMode} size="small">
            {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </LogoContainer>

      <NewChatButton 
        onClick={handleNewChat}
        startIcon={<AddIcon />}
        variant="contained"
      >
        New Chat
      </NewChatButton>

      {/* Main navigation */}
      <List disablePadding sx={{ px: 1 }}>
        <NavItem
          selected={location.pathname === '/'}
          onClick={() => handleNavigation('/')}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ChatIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Chat" />
        </NavItem>
        
        <NavItem
          selected={location.pathname === '/watchlist'}
          onClick={() => handleNavigation('/watchlist')}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <WatchlistIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Watchlist" />
        </NavItem>
      </List>
      
      <Divider sx={{ my: 1.5 }} />
      
      {/* Chat sessions */}
      <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}
        >
          Recent Chats
        </Typography>
        
        <Tooltip title="Organize chats into folders (coming soon)">
          <IconButton size="small" disabled>
            <FolderIcon fontSize="small" sx={{ opacity: 0.5 }} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <SessionList disablePadding>
        {sessions.length === 0 ? (
          <Box sx={{ 
            px: 3, 
            py: 2, 
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontStyle: 'italic',
            opacity: 0.7
          }}>
            No chats yet
          </Box>
        ) : (
          sessions.map((session, index) => (
            <SessionItem
              key={`session-${session.id}-${index}`}
              selected={session.id === activeId}
              onClick={() => handleSessionClick(session.id)}
              sx={{ 
                display: 'flex',
                justifyContent: 'space-between'
              }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                <Typography 
                  noWrap 
                  sx={{ 
                    fontWeight: session.id === activeId ? 600 : 400 
                  }}
                >
                  {truncateTitle(session.title)}
                </Typography>
                
                {session.lastUpdatedAt && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {new Date(session.lastUpdatedAt.toDate()).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ visibility: session.id === activeId ? 'visible' : 'hidden' }}>
                <Tooltip title="Delete chat">
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleDeleteSession(e, session.id)}
                    sx={{ opacity: 0.6 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </SessionItem>
          ))
        )}
      </SessionList>

      {/* Secondary navigation */}
      <Box sx={{ px: 1, mb: 1 }}>
        <NavItem onClick={() => handleNavigation('/settings')}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </NavItem>
        
        <NavItem onClick={() => handleNavigation('/about')}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="About" />
        </NavItem>
      </Box>

      {/* User footer */}
      <Footer>
        {currentUser ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setShowAcct(true)}
          >
            <Avatar sx={{ mr: 1 }}>
              {(currentUser.displayName || currentUser.email)[0].toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              <Typography noWrap>
                {currentUser.displayName || currentUser.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Premium Plan
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowAuth(true)}
          >
            <LoginIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography>Sign In</Typography>
          </Box>
        )}
      </Footer>
    </SidebarContainer>
  );

  // Mobile hamburger button
  if (isMobile) {
    return (
      <>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ 
            position: 'fixed', 
            top: 12, 
            left: 12, 
            zIndex: 1200,
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              bgcolor: 'background.paper',
            }
          }}
        >
          <MenuIcon />
        </IconButton>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box'
            }
          }}
        >
          {sidebarContent}
        </Drawer>
        
        {/* Auth dialogs */}
        <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} />
        <AccountDialog open={showAcct} onClose={() => setShowAcct(false)} />
        
        {/* Confirmation dialog for deleting a session */}
        <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, sessionId: null })}>
          <DialogTitle>Delete Chat</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this chat? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete({ open: false, sessionId: null })}>Cancel</Button>
            <Button color="error" onClick={confirmDeleteSession}>Delete</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box'
          }
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Auth dialogs */}
      <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} />
      <AccountDialog open={showAcct} onClose={() => setShowAcct(false)} />
      
      {/* Confirmation dialog for deleting a session */}
      <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, sessionId: null })}>
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this chat? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, sessionId: null })}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteSession}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}